import React, { useState, useRef, useEffect } from 'react';
import {
    useGetThreatFeedPostsQuery,
    useGetIntelActivityQuery,
    useGetThreatOverviewQuery
} from '@/lib/services/threatFeedsApi';
import IntelActivityBarChart from '@/components/features/threat-feeds/IntelActivityBarChart';
import { Filter, RotateCcw, ChevronDown, Search, AlertTriangle, X, Copy, CheckCircle2, Globe, Loader2 } from 'lucide-react';

// --- MOCK DATA FOR DROPDOWNS ---
const CATEGORIES = ['Access Sale', 'Alert', 'Combo List', 'Cyber Attack', 'Data Breach', 'Data Leak'];
const NETWORKS = ['Clearnet', 'Discord', 'Open Web', 'Telegram', 'Test Network', 'Tor'];
const INDUSTRIES = ['Accounting', 'Advertising/Marketing', 'Agriculture & Farming', 'Airlines & Aviation', 'Alternative Dispute Resolution', 'Alternative Medicine', 'Financial Services', 'Government'];
const THREAT_ACTORS = ['[Manager] Punk', '0apt', '0kb', 'Omid16b', 'OxOxba$e', '303', 'NoName057(16)', 'GRU'];

// Fallback list just in case the REST Countries API is blocked by a firewall
const FALLBACK_COUNTRIES = [
    "Afghanistan", "Albania", "Algeria", "Argentina", "Australia", "Austria", "Bahamas", "Bahrain",
    "Bangladesh", "Belarus", "Belgium", "Brazil", "Bulgaria", "Canada", "Chile", "China", "Colombia",
    "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Egypt", "Estonia", "Finland", "France",
    "Germany", "Greece", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel",
    "Italy", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kuwait", "Latvia", "Lebanon", "Libya", "Lithuania",
    "Malaysia", "Mexico", "Morocco", "Netherlands", "New Zealand", "Nigeria", "North Korea", "Norway",
    "Oman", "Pakistan", "Palestine", "Peru", "Philippines", "Poland", "Portugal", "Qatar", "Romania",
    "Russia", "Rwanda", "Saudi Arabia", "Serbia", "Singapore", "Slovakia", "Slovenia", "South Africa",
    "South Korea", "Spain", "Sweden", "Switzerland", "Syria", "Taiwan", "Thailand", "Turkey", "Ukraine",
    "United Arab Emirates", "United Kingdom", "United States", "Venezuela", "Vietnam", "Yemen"
];

// --- CUSTOM DROPDOWN COMPONENT ---
interface DropdownProps {
    label: string;
    value: string;
    options: string[];
    onChange: (val: string) => void;
    searchable?: boolean;
}

const FilterDropdown: React.FC<DropdownProps> = ({ label, value, options, onChange, searchable = true }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const filteredOptions = options.filter(opt => opt.toLowerCase().includes(query.toLowerCase()));

    return (
        <div className="relative" ref={dropdownRef}>
            <div
                onClick={() => setIsOpen(!isOpen)}
                className={`bg-[#0A0B0E] border ${isOpen ? 'border-[#50A970]' : 'border-[#1A1D24]'} text-slate-400 text-[11px] px-4 py-2.5 rounded-lg flex items-center justify-between min-w-[140px] cursor-pointer hover:border-slate-500 transition-colors`}
            >
                <span>{label}: <span className="text-white ml-1 truncate max-w-[80px] inline-block align-bottom">{value}</span></span>
                <ChevronDown className={`w-3 h-3 ml-2 transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#50A970]' : ''}`} />
            </div>

            {isOpen && (
                <div className="absolute top-full left-0 mt-2 w-full min-w-[200px] bg-[#0A0B0E] border border-[#1A1D24] rounded-lg shadow-[0_4px_20px_rgba(0,0,0,0.8)] z-50 overflow-hidden flex flex-col">
                    {searchable && (
                        <div className="p-2 border-b border-[#1A1D24]">
                            <div className="bg-[#13171F] rounded flex items-center px-2 py-1.5">
                                <Search className="w-3 h-3 text-slate-500 mr-2 shrink-0" />
                                <input
                                    type="text"
                                    placeholder="Type to filter"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    className="bg-transparent border-none text-[11px] text-white focus:outline-none w-full placeholder-slate-600"
                                />
                            </div>
                        </div>
                    )}
                    <div className="max-h-[200px] overflow-y-auto custom-scrollbar p-1">
                        <div
                            onClick={() => { onChange('All'); setIsOpen(false); setQuery(''); }}
                            className={`px-3 py-2 text-[11px] rounded cursor-pointer transition-colors ${value === 'All' ? 'text-[#50A970] bg-[#50A970]/10' : 'text-slate-300 hover:bg-[#161A23]'}`}
                        >
                            All
                        </div>
                        {filteredOptions.map((opt) => (
                            <div
                                key={opt}
                                onClick={() => { onChange(opt); setIsOpen(false); setQuery(''); }}
                                className={`px-3 py-2 text-[11px] rounded cursor-pointer transition-colors ${value === opt ? 'text-[#50A970] bg-[#50A970]/10' : 'text-slate-300 hover:bg-[#161A23]'}`}
                            >
                                {opt}
                            </div>
                        ))}
                        {filteredOptions.length === 0 && (
                            <div className="px-3 py-4 text-[11px] text-slate-600 text-center">No matches found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- MAIN COMPONENT ---
const DarkWebChatters: React.FC = () => {
    const [filters, setFilters] = useState({
        search: '', date: 'All', network: 'All', category: 'All', industry: 'All', threatActor: 'All', country: 'All', domain: '', tags: '', site: ''
    });

    const [searchInput, setSearchInput] = useState('');
    const [countryOptions, setCountryOptions] = useState<string[]>(FALLBACK_COUNTRIES);
    const [selectedPost, setSelectedPost] = useState<any | null>(null);

    const [zoomedScreenshot, setZoomedScreenshot] = useState<string | null>(null);
    const [pendingUrl, setPendingUrl] = useState<string | null>(null);
    const [copied, setCopied] = useState(false);

    const isFilterActive = Object.entries(filters).some(([v]) => v !== 'All' && v !== '');

    useEffect(() => {
        fetch('https://restcountries.com/v3.1/all?fields=name')
            .then(res => res.json())
            .then(data => {
                if (Array.isArray(data)) setCountryOptions(data.map((c: any) => c.name.common).sort());
            })
            .catch(() => console.warn("Using fallback countries."));
    }, []);

    const queryParams: Record<string, any> = { limit: 10 };
    if (filters.search && filters.search.length >= 4) queryParams.search = filters.search;
    if (filters.network !== 'All') queryParams.network = filters.network;
    if (filters.category !== 'All') queryParams.category = filters.category;
    if (filters.industry !== 'All') queryParams.industry = filters.industry;
    if (filters.threatActor !== 'All') queryParams.threat_actor = filters.threatActor;
    if (filters.country !== 'All') queryParams.country = filters.country;
    if (filters.domain) queryParams.domain = filters.domain;
    if (filters.site) queryParams.site = filters.site;
    if (filters.tags) queryParams.tags = filters.tags;

    if (filters.date !== 'All') {
        const days = parseInt(filters.date.split(' ')[0]);
        if (!isNaN(days)) {
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - days);
            queryParams.start_date = startDate.toISOString().split('T')[0];
            queryParams.end_date = new Date().toISOString().split('T')[0];
        }
    }

    const { data: postsData, isLoading: isPostsLoading } = useGetThreatFeedPostsQuery(queryParams);
    const { data: activityData, isLoading: isActivityLoading } = useGetIntelActivityQuery(90);
    const { data: overviewData, isLoading: isOverviewLoading } = useGetThreatOverviewQuery();

    const lastSyncString = overviewData?.meta?.last_sync_utc ? `${overviewData.meta.last_sync_utc} UTC` : 'AWAITING TELEMETRY...';
    const systemStatus = overviewData?.status ? overviewData.status.toUpperCase() : 'ELEVATED';
    const statusColorClass = systemStatus === 'SUCCESS' ? 'text-[#50A970]' : 'text-[#FF3333]';

    const handleSearchClick = () => setFilters(prev => ({ ...prev, search: searchInput }));
    const handleKeyDown = (e: React.KeyboardEvent) => e.key === 'Enter' && handleSearchClick();
    const handleReset = () => {
        setSearchInput('');
        setFilters({ search: '', date: 'All', network: 'All', category: 'All', industry: 'All', threatActor: 'All', country: 'All', domain: '', tags: '', site: '' });
    };
    const updateFilter = (key: keyof typeof filters, value: string) => setFilters(prev => ({ ...prev, [key]: value }));

    const copyToClipboard = (text: string | null) => {
        if (!text) return;
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    // --- Resilient JSON Parsing for Screenshots ---
    let parsedScreenshots: string[] = [];
    if (selectedPost?.screenshots_json) {
        if (Array.isArray(selectedPost.screenshots_json)) {
            parsedScreenshots = selectedPost.screenshots_json;
        } else if (typeof selectedPost.screenshots_json === 'string') {
            try {
                const parsed = JSON.parse(selectedPost.screenshots_json);
                if (Array.isArray(parsed)) parsedScreenshots = parsed;
            } catch (e) {
                console.error("Failed to parse screenshots JSON string:", e);
            }
        }
    }

    return (
        <div className="flex flex-col gap-6 p-8 min-h-screen bg-black text-white font-mono relative">

            {/* ✅ GLOBAL PIPELINE LOAD OVERLAY: Displays systemic synchronization workflow frames until endpoints resolve successfully */}
            {(isPostsLoading || isActivityLoading || isOverviewLoading) && (
                <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-[99999] flex flex-col items-center justify-center gap-4 animate-in fade-in duration-300 pl-[70px] lg:pl-[240px]">
                    <div className="p-6 bg-[#0A0B0E] border border-[#1A1D24] rounded-xl flex flex-col items-center max-w-xs text-center shadow-2xl relative overflow-hidden">
                        <div className="absolute -top-10 -left-10 w-24 h-24 bg-[#50A970]/5 rounded-full pointer-events-none blur-xl"></div>
                        <Loader2 className="h-9 w-9 text-[#50A970] animate-spin mb-3.5" />
                        <h3 className="text-[12px] font-bold text-white uppercase tracking-wider">Intercepting Dark Web</h3>
                        <p className="text-[10px] text-slate-500 font-mono mt-1.5 leading-relaxed">
                            Parsing subterranean text clusters and indexing data sequences...
                        </p>
                    </div>
                </div>
            )}

            <div className="flex justify-between items-end mb-2">
                <div>
                    <h1 className="text-[22px] font-bold text-white tracking-wide">Threats Feeds</h1>
                    <p className={`text-[10px] mt-1 font-semibold uppercase tracking-widest ${statusColorClass}`}>
                        System_Status: {isOverviewLoading ? 'SYNCING...' : systemStatus}
                    </p>
                </div>
                <div className="flex items-center gap-2 text-[10px] text-slate-500 uppercase tracking-wider">
                    <span>Last_Sync: {isOverviewLoading ? 'Fetching...' : lastSyncString}</span>
                    <span className="w-2 h-2 rounded-full bg-[#50A970] shadow-[0_0_8px_rgba(80,169,112,0.8)] animate-pulse"></span>
                </div>
            </div>

            <div className="flex items-center gap-3 w-full relative z-30">
                <input type="text" placeholder="Search news titles and summaries..." value={searchInput} onChange={(e) => setSearchInput(e.target.value)} onKeyDown={handleKeyDown} className="flex-1 bg-[#0A0B0E] border border-[#1A1D24] text-sm text-white px-4 py-2.5 rounded-lg focus:outline-none focus:border-[#50A970] transition-colors placeholder-slate-600" />
                <button onClick={handleSearchClick} className="bg-[#50A970] hover:bg-[#3d8556] text-black font-bold px-6 py-2.5 rounded-lg transition-colors text-[13px]">Search</button>
                <button className={`border px-5 py-2.5 rounded-lg transition-colors text-[13px] flex items-center gap-2 pointer-events-none ${isFilterActive ? 'bg-[#50A970]/10 border-[#50A970] text-[#50A970]' : 'bg-[#0A0B0E] border-[#1A1D24] text-slate-300 opacity-50'}`}>
                    <Filter className="w-4 h-4" /> {isFilterActive ? 'Filters Active' : 'No Filters'}
                </button>
                <FilterDropdown label="Date" value={filters.date} options={['7 Days', '30 Days', '90 Days']} onChange={(val) => updateFilter('date', val)} searchable={false} />
                <button onClick={handleReset} className="bg-[#0A0B0E] border border-[#1A1D24] hover:bg-[#161A23] text-slate-300 px-5 py-2.5 rounded-lg transition-colors text-[13px] flex items-center gap-2">
                    <RotateCcw className="w-4 h-4" /> Reset All
                </button>
            </div>

            <div className="flex flex-wrap items-center gap-3 relative z-20">
                <FilterDropdown label="Network" value={filters.network} options={NETWORKS} onChange={(val) => updateFilter('network', val)} />
                <FilterDropdown label="Category" value={filters.category} options={CATEGORIES} onChange={(val) => updateFilter('category', val)} />
                <FilterDropdown label="Industry" value={filters.industry} options={INDUSTRIES} onChange={(val) => updateFilter('industry', val)} />
                <FilterDropdown label="Threat Actor" value={filters.threatActor} options={THREAT_ACTORS} onChange={(val) => updateFilter('threatActor', val)} />
                <FilterDropdown label="Country" value={filters.country} options={countryOptions} onChange={(val) => updateFilter('country', val)} />
                <div className={`bg-[#0A0B0E] border ${filters.domain ? 'border-[#50A970]' : 'border-[#1A1D24]'} text-slate-400 text-[11px] px-4 py-2.5 rounded-lg flex items-center min-w-[140px] focus-within:border-[#50A970] transition-colors`}>
                    <span>Domain: <input type="text" placeholder="Victim" value={filters.domain} onChange={(e) => updateFilter('domain', e.target.value)} onKeyDown={handleKeyDown} className="bg-transparent border-none text-white ml-2 focus:outline-none w-16 placeholder-slate-600" /></span>
                </div>
                <div className={`bg-[#0A0B0E] border ${filters.tags ? 'border-[#50A970]' : 'border-[#1A1D24]'} text-slate-400 text-[11px] px-4 py-2.5 rounded-lg flex items-center min-w-[140px] focus-within:border-[#50A970] transition-colors`}>
                    <span>Tags: <input type="text" placeholder="ddos" value={filters.tags} onChange={(e) => updateFilter('tags', e.target.value)} onKeyDown={handleKeyDown} className="bg-transparent border-none text-white ml-2 focus:outline-none w-16 placeholder-slate-600" /></span>
                </div>
                <div className={`bg-[#0A0B0E] border ${filters.site ? 'border-[#50A970]' : 'border-[#1A1D24]'} text-slate-400 text-[11px] px-4 py-2.5 rounded-lg flex items-center min-w-[140px] focus-within:border-[#50A970] transition-colors`}>
                    <span>Site: <input type="text" placeholder="Victim site" value={filters.site} onChange={(e) => updateFilter('site', e.target.value)} onKeyDown={handleKeyDown} className="bg-transparent border-none text-white ml-2 focus:outline-none w-20 placeholder-slate-600" /></span>
                </div>
            </div>

            <div className="flex flex-col gap-3 relative z-10">
                {postsData?.results?.length === 0 ? (
                    <div className="py-12 flex items-center justify-center text-xs text-slate-600 border border-dashed border-[#1A1D24] rounded-xl">No chatter intercepted matching these filters.</div>
                ) : (
                    postsData?.results?.map((post) => (
                        <div key={post.uuid || post.id} onClick={() => setSelectedPost(post)} className="bg-[#0A0B0E] border border-[#1A1D24] hover:border-[#50A970]/50 hover:bg-[#10131A] transition-all p-5 rounded-xl flex flex-col gap-2 cursor-pointer">
                            <div className="text-[10px] text-slate-500 uppercase tracking-widest">{post.published_at?.split('T')[0] || 'Unknown Date'}</div>
                            <h3 className="text-[15px] font-bold text-slate-200 tracking-wide">{post.title}</h3>
                            <div className="flex items-center gap-2 mt-1">
                                {post.category && <span className="bg-[#161A23] text-slate-400 text-[9px] px-2 py-1 rounded font-semibold tracking-wider">{post.category}</span>}
                                {post.victim_country && <span className="bg-[#161A23] text-slate-400 text-[9px] px-2 py-1 rounded font-semibold tracking-wider">{post.victim_country}</span>}
                                {post.network && <span className="bg-[#161A23] text-slate-400 text-[9px] px-2 py-1 rounded font-semibold tracking-wider">{post.network}</span>}
                            </div>
                        </div>
                    ))
                )}
            </div>

            <div className="bg-[#0A0B0E] border border-[#1A1D24] rounded-xl p-6 shadow-lg h-[300px] flex flex-col relative mt-2 z-10">
                <div className="flex justify-between items-end mb-6 z-10">
                    <h2 className="text-[13px] font-bold tracking-wide text-white">Intel Activity</h2>
                    <div className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold">Range: All-time</div>
                </div>
                <div className="flex-1 relative">
                    <IntelActivityBarChart data={activityData?.intel_activity_timeline} isLoading={isActivityLoading} />
                </div>
            </div>

            {/* --- FIXED RESPONSIVE MODAL OVERLAY --- */}
            {selectedPost && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4 pl-[70px] lg:pl-[340px] transition-all duration-300">
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] shadow-[0_0_60px_rgba(0,0,0,0.95)] rounded-2xl w-full max-w-[500px] max-h-[90vh] flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200">

                        <div className="p-6 pb-5 border-b border-[#1A1D24]/60 relative shrink-0 overflow-hidden">
                            <svg className="absolute -top-4 -left-4 w-64 h-64 -translate-x-1/4 -translate-y-1/4 opacity-[0.03] pointer-events-none" viewBox="0 0 100 100">
                                <circle cx="50" cy="50" r="40" fill="none" stroke="white" strokeWidth="1.5" />
                                <circle cx="50" cy="50" r="30" fill="none" stroke="white" strokeWidth="1.5" />
                                <circle cx="50" cy="50" r="20" fill="none" stroke="white" strokeWidth="1.5" />
                                <circle cx="50" cy="50" r="10" fill="none" stroke="white" strokeWidth="1.5" />
                            </svg>

                            <button onClick={() => setSelectedPost(null)} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors p-1 z-10">
                                <X className="w-5 h-5" />
                            </button>

                            <div className="w-12 h-12 border border-[#1A1D24] bg-[#0A0B0E] rounded-full flex items-center justify-center mb-4 relative z-10 shadow-inner">
                                <AlertTriangle className="w-5 h-5 text-slate-300" />
                            </div>

                            <h2 className="text-[17px] font-bold text-white mb-1.5 leading-snug pr-8 relative z-10">{selectedPost.title}</h2>
                            <p className="text-[10px] text-slate-500 font-mono uppercase tracking-widest relative z-10">
                                Threat Feed — {selectedPost.id || selectedPost.uuid?.substring(0, 8)}
                            </p>
                        </div>

                        <div className="p-5 flex flex-col gap-3.5 overflow-y-auto custom-scrollbar flex-1 bg-[#0A0B0E]">

                            <div className="bg-[#12151C] border border-[#1A1D24] rounded-xl p-4">
                                <h3 className="text-[12px] font-bold text-white mb-2.5">Content</h3>
                                <p className="text-[11px] text-slate-400 leading-relaxed font-sans whitespace-pre-wrap">
                                    {selectedPost.content || selectedPost.title || 'No additional body content provided for this intercept.'}
                                </p>
                            </div>

                            <div className="bg-[#12151C] border border-[#1A1D24] rounded-xl p-4">
                                <h3 className="text-[12px] font-bold text-white mb-3">Published</h3>

                                <div className="flex justify-between items-center text-[11px] mb-2.5 border-b border-[#1A1D24]/50 pb-2.5">
                                    <span className="text-slate-400 font-sans">Source URL</span>
                                    <div className="flex items-center gap-2 max-w-[200px]">
                                        {selectedPost.published_url ? (
                                            /* ✅ INTERCEPTED SECURE LINK TRIGGER: Open threat wall modal warning frame configuration directly when clicked */
                                            <button 
                                                onClick={() => setPendingUrl(selectedPost.published_url)}
                                                className="text-[#3B82F6] hover:text-[#60A5FA] truncate transition-colors font-sans text-left focus:outline-none"
                                            >
                                                {selectedPost.published_url}
                                            </button>
                                        ) : (
                                            <span className="text-slate-600 font-sans">Unknown</span>
                                        )}
                                        {selectedPost.published_url && (
                                            <button onClick={() => setPendingUrl(selectedPost.published_url)} className="text-slate-500 hover:text-white transition-colors flex-shrink-0" title="Open Link">
                                                <Globe className="w-3.5 h-3.5" />
                                            </button>
                                        )}
                                    </div>
                                </div>

                                <div className="flex justify-between items-center text-[11px]">
                                    <span className="text-slate-400 font-sans">Date</span>
                                    <span className="text-slate-300 font-medium font-sans">{selectedPost.published_at?.split('T')[0] || 'Unknown'}</span>
                                </div>
                            </div>

                            <div className="bg-[#12151C] border border-[#1A1D24] rounded-xl p-4">
                                <h3 className="text-[12px] font-bold text-white mb-3">Meta Data</h3>
                                <div className="flex flex-col">
                                    {[
                                        { label: 'Network', value: selectedPost.network },
                                        { label: 'Victim Org', value: selectedPost.victim_organization },
                                        { label: 'Victim Site', value: selectedPost.victim_site },
                                        { label: 'Victim Domain', value: selectedPost.victim_domain },
                                        { label: 'Industry', value: selectedPost.victim_industry },
                                        { label: 'Country', value: selectedPost.victim_country },
                                        { label: 'Threat Actor', value: selectedPost.threat_actor_names_cached }
                                    ].map((meta, i, arr) => (
                                        <div key={meta.label} className={`flex justify-between items-center text-[11px] py-1.5 ${i !== arr.length - 1 ? 'border-b border-[#1A1D24]/50' : ''}`}>
                                            <span className="text-slate-400 font-sans">{meta.label}</span>
                                            <span className="text-slate-300 font-medium truncate max-w-[150px] font-sans">{meta.value || 'Unknown'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {parsedScreenshots.length > 0 && (
                                <div className="bg-[#12151C] border border-[#1A1D24] rounded-xl p-4">
                                    <h3 className="text-[12px] font-bold text-white mb-3">Screenshots</h3>
                                    <div className="flex gap-3 overflow-x-auto custom-scrollbar pb-2">
                                        {parsedScreenshots.map((imgUrl, index) => (
                                            <img
                                                key={index}
                                                src={imgUrl}
                                                alt={`Intercept Screenshot ${index + 1}`}
                                                onClick={() => setZoomedScreenshot(imgUrl)}
                                                className="h-24 w-auto object-cover rounded border border-[#1A1D24]/60 cursor-pointer hover:border-[#50A970] transition-all hover:opacity-80 shrink-0"
                                                title="Click to zoom"
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className="flex justify-end mt-1">
                                <button
                                    onClick={() => setSelectedPost(null)}
                                    className="px-5 py-2 rounded-lg bg-[#161A23] border border-[#1A1D24] hover:bg-[#1A1D24] text-slate-300 text-[11px] font-medium transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* --- ZOOMED SCREENSHOT FULLSCREEN OVERLAY --- */}
            {zoomedScreenshot && (
                <div
                    className="fixed inset-0 z-[10100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-zoom-out animate-in fade-in duration-200 pl-[70px] lg:pl-[240px]"
                    onClick={() => setZoomedScreenshot(null)}
                >
                    <div className="relative max-w-[90vw] max-h-[90vh] flex items-center justify-center">
                        <button
                            onClick={(e) => { e.stopPropagation(); setZoomedScreenshot(null); }}
                            className="absolute -top-12 -right-12 text-white/50 hover:text-white transition-colors p-2"
                            title="Close"
                        >
                            <X className="w-8 h-8" />
                        </button>
                        <img
                            src={zoomedScreenshot}
                            alt="Zoomed Intercept"
                            className="max-w-full max-h-[85vh] object-contain rounded-lg border border-[#1A1D24] shadow-2xl cursor-default animate-in zoom-in-95 duration-200"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>
            )}

            {/* --- EXTERNAL URL REDIRECTION WARNING MODAL --- */}
            {pendingUrl && (
                <div className="fixed inset-0 z-[10200] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 pl-[70px] lg:pl-[240px]">
                    <div className="bg-[#0A0B0E] border border-[#1A1D24] shadow-[0_0_60px_rgba(255,51,51,0.15)] rounded-2xl w-full max-w-[440px] flex flex-col relative overflow-hidden animate-in fade-in zoom-in-95 duration-200 p-6">

                        <svg className="absolute top-0 left-0 w-64 h-64 -translate-x-1/4 -translate-y-1/4 opacity-[0.05] pointer-events-none" viewBox="0 0 100 100">
                            <circle cx="50" cy="50" r="40" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                            <circle cx="50" cy="50" r="30" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                            <circle cx="50" cy="50" r="20" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                            <circle cx="50" cy="50" r="10" fill="none" stroke="#FF3333" strokeWidth="1.5" />
                        </svg>

                        <button onClick={() => { setPendingUrl(null); setCopied(false); }} className="absolute top-5 right-5 text-slate-600 hover:text-white transition-colors p-1 z-10">
                            <X className="w-5 h-5" />
                        </button>

                        <div className="w-12 h-12 border border-[#FF3333]/30 bg-[#FF3333]/10 rounded-xl flex items-center justify-center mb-5 relative z-10 shadow-inner">
                            <AlertTriangle className="w-5 h-5 text-[#FF3333]" />
                        </div>

                        <h2 className="text-[18px] font-sans font-bold text-white mb-3 relative z-10">External Redirection Warning</h2>
                        <p className="text-[12px] text-slate-400 font-mono leading-relaxed mb-5 relative z-10">
                            You are about to leave the platform and visit an external URL. This link often leads to a hacker forum, leak site, or unverified source.
                        </p>

                        <div className="bg-[#12151C] border border-[#FF3333]/30 rounded-lg p-3.5 mb-5 relative z-10 flex items-center gap-3 overflow-hidden shadow-inner">
                            <p className="text-[12px] text-[#FF6B6B] font-mono truncate flex-1" title={pendingUrl}>{pendingUrl}</p>
                            <button
                                onClick={() => copyToClipboard(pendingUrl)}
                                className="text-slate-500 hover:text-white transition-colors shrink-0 p-1"
                                title="Copy URL"
                            >
                                {copied ? <CheckCircle2 className="w-4 h-4 text-[#50A970]" /> : <Copy className="w-4 h-4" />}
                            </button>
                        </div>

                        <p className="text-[11px] text-slate-500 font-mono leading-relaxed mb-6 relative z-10">
                            Security Tip: We recommend opening this link in a sandboxed environment or a dedicated VM.
                        </p>

                        <div className="flex justify-end gap-3 relative z-10">
                            <button
                                onClick={() => { setPendingUrl(null); setCopied(false); }}
                                className="px-5 py-2.5 rounded-lg bg-[#161A23] border border-[#1A1D24] hover:bg-[#1A1D24] text-slate-300 text-[12px] font-bold tracking-wide transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    window.open(pendingUrl, '_blank', 'noopener,noreferrer');
                                    setPendingUrl(null);
                                    setCopied(false);
                                }}
                                className="px-5 py-2.5 rounded-lg bg-[#DE3535] hover:bg-[#FF4444] text-white text-[12px] font-bold tracking-wide transition-colors"
                            >
                                Proceed (Unsafe)
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style dangerouslySetInnerHTML={{
                __html: `
                .custom-scrollbar::-webkit-scrollbar { width: 4px; height: 4px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #1A1D24; border-radius: 4px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #50A970; }
            `}} />
        </div>
    );
};

export default DarkWebChatters;